import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Ban, Loader2, ShieldCheck, Info } from 'lucide-react';

/**
 * Minimal shape both roster pages can satisfy for ban/unban actions.
 */
export interface BanTarget {
  id: string;
  full_name: string;
  /** True when the target has a scheduled (not-yet-fired) ban. */
  pending_ban_at?: string | null;
}

interface BanUserDialogsProps {
  /** User the admin is about to permanently ban (null = dialog closed). */
  banTarget: BanTarget | null;
  /** User the admin is about to un-ban (null = dialog closed). */
  unbanTarget: BanTarget | null;
  onCloseBan: () => void;
  onCloseUnban: () => void;
  /** Called after a successful ban or unban so the page can refetch. */
  onDone: () => void;
  /** "driver" | "passenger" — used in copy only. */
  roleLabel: string;
}

/**
 * Shared permanent-ban + lift-ban confirmation dialogs used by the
 * Drivers and Passengers admin pages. Bans call `ban_user_admin`,
 * which suspends the account AND blocklists its phone, email, license
 * numbers, and vehicle plate so the person cannot register a fresh
 * account. Lift-ban (`unban_user_admin`) fully reverses all of it.
 */
export function BanUserDialogs({
  banTarget, unbanTarget, onCloseBan, onCloseUnban, onDone, roleLabel,
}: BanUserDialogsProps) {
  const [banReason, setBanReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleBan = async () => {
    if (!banTarget) return;
    if (banReason.trim().length < 3) {
      toast.error('Reason must be at least 3 characters');
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('ban_user_admin', {
        p_user_id: banTarget.id,
        p_reason: banReason.trim(),
      });
      if (error) throw error;
      const res = data as any;
      if (res?.ok === false) throw new Error(res.error || 'Ban failed');
      if (res?.deferred) {
        // Safety deferral: the user is mid-trip; the ban is armed and
        // fires automatically when their last active trip ends.
        toast.info(
          `${banTarget.full_name} is on an active trip. The ban is scheduled and will apply automatically the moment their trip safely ends.`,
          { duration: 8000 },
        );
      } else {
        toast.success(`${banTarget.full_name} permanently banned`);
      }
      setBanReason('');
      onCloseBan();
      onDone();
    } catch (err: any) {
      toast.error(err.message || 'Failed to ban user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnban = async () => {
    if (!unbanTarget) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('unban_user_admin', {
        p_user_id: unbanTarget.id,
      });
      if (error) throw error;
      const res = data as any;
      if (res?.ok === false) throw new Error(res.error || 'Unban failed');
      toast.success(
        res?.was_pending
          ? `Scheduled ban canceled for ${unbanTarget.full_name}`
          : `Ban lifted for ${unbanTarget.full_name}`,
      );
      onCloseUnban();
      onDone();
    } catch (err: any) {
      toast.error(err.message || 'Failed to lift ban');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Permanent ban */}
      <Dialog open={!!banTarget} onOpenChange={(open) => { if (!open) { setBanReason(''); onCloseBan(); } }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-400" /> Permanently Ban {roleLabel === 'driver' ? 'Driver' : 'Passenger'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {banTarget?.full_name} will be locked out immediately, and their phone number, email,
              license numbers, and vehicle plate will be blocklisted so they cannot create a new
              account with the same identity. If they are on an active trip right now, the ban is
              scheduled and applies automatically the moment the trip safely ends, so passengers are
              never stranded mid-ride.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-start gap-2 bg-red-500/5 border border-red-500/20 rounded-lg p-3">
              <Info className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              <p className="text-xs text-red-300">
                This is stronger than a suspension: new signups reusing any of their identities are
                blocked at the database level. An admin can reverse it at any time with "Lift ban".
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">
                Reason <span className="text-red-400">*</span>
              </Label>
              <Textarea
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white min-h-[80px]"
                placeholder="e.g. Repeated fraud, serious safety incident, ban evasion"
              />
              {banReason.length > 0 && banReason.trim().length < 3 && (
                <p className="text-xs text-red-400">Reason must be at least 3 characters</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setBanReason(''); onCloseBan(); }} className="text-gray-400">
              Cancel
            </Button>
            <Button
              onClick={handleBan}
              disabled={submitting || banReason.trim().length < 3}
              className="bg-red-700 hover:bg-red-600 text-white"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Ban Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lift ban */}
      <Dialog open={!!unbanTarget} onOpenChange={(open) => { if (!open) onCloseUnban(); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
              {unbanTarget?.pending_ban_at ? 'Cancel Scheduled Ban' : 'Lift Ban'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {unbanTarget?.pending_ban_at
                ? `Cancel the scheduled ban on ${unbanTarget?.full_name}? The ban has not fired yet (they are or were mid-trip); canceling now means it never will.`
                : `Lift the permanent ban on ${unbanTarget?.full_name}? Their account is reinstated, the suspension is cleared, and all of their blocklisted identities are released.`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <div className="flex items-start gap-2 bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
              <Info className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-300">
                {unbanTarget?.pending_ban_at
                  ? `The ${roleLabel} keeps normal access. They were never locked out while the ban was pending. The scheduling action stays in the audit log.`
                  : `The ${roleLabel} can sign in and use the app again immediately. The ban history stays in the audit log.`}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={onCloseUnban} className="text-gray-400">
              Cancel
            </Button>
            <Button
              onClick={handleUnban}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Lift Ban
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
